import { IAuthRepository } from './IAuthRepository'
import { User, AuthResponse, AuthError, UserProfile, UserRole } from '../models/User'
import authService from '../../services/authService'
import { profileService } from '../../services/back4app'

export class Back4AppAuthRepository implements IAuthRepository {
  async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await authService.signIn(email, password)
      
      return {
        user: {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
          createdAt: response.user.createdAt
        },
        profile: response.profile ? {
          firstName: response.profile.firstName,
          lastName: response.profile.lastName,
          title: response.profile.title,
          contact: response.profile.contact,
          bio: response.profile.bio,
          avatar: response.profile.avatar,
          gameCharacter: response.profile.gameCharacter,
          customAvatarUrl: response.profile.customAvatarUrl,
          role: response.profile.role,
          profileCompleted: response.profile.profileCompleted
        } : undefined
      }
    } catch (error: any) {
      throw {
        code: error.code || 999,
        message: error.message || 'Sign in failed',
        field: error.field
      } as AuthError
    }
  }

  async signUp(username: string, email: string, password: string, profile?: Partial<UserProfile>): Promise<AuthResponse> {
    try {
      const response = await authService.signUp(username, email, password, profile)
      
      return {
        user: {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
          createdAt: response.user.createdAt
        },
        profile: response.profile ? {
          firstName: response.profile.firstName,
          lastName: response.profile.lastName,
          title: response.profile.title,
          contact: response.profile.contact,
          bio: response.profile.bio,
          avatar: response.profile.avatar,
          gameCharacter: response.profile.gameCharacter,
          customAvatarUrl: response.profile.customAvatarUrl,
          role: response.profile.role,
          profileCompleted: response.profile.profileCompleted
        } : undefined
      }
    } catch (error: any) {
      throw {
        code: error.code || 999,
        message: error.message || 'Sign up failed',
        field: error.field
      } as AuthError
    }
  }

  async signOut(): Promise<void> {
    try {
      await authService.signOut()
    } catch (error: any) {
      throw {
        code: error.code || 999,
        message: error.message || 'Sign out failed'
      } as AuthError
    }
  }

  async checkSession(): Promise<User | null> {
    try {
      const user = await authService.checkSession()
      if (!user) return null
      
      // Get profile to complete User object
      const profile = await authService.getCurrentProfile()
      
      return {
        id: user.id,
        username: user.username,
        email: user.email,
        role: (profile?.role as UserRole) || UserRole.USER,
        createdAt: user.createdAt.toISOString(),
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        title: profile?.title,
        bio: profile?.bio,
        contact: profile?.contact,
        avatar: profile?.avatar,
        gameCharacter: profile?.gameCharacter,
        customAvatarUrl: profile?.customAvatarUrl,
        profileCompleted: profile?.profileCompleted
      }
    } catch (error) {
      return null
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const user = authService.getCurrentUser()
      if (!user) return null
      
      // Get profile to complete User object
      const profile = await authService.getCurrentProfile()
      
      return {
        id: user.id,
        username: user.username,
        email: user.email,
        role: (profile?.role as UserRole) || UserRole.USER,
        createdAt: user.createdAt.toISOString(),
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        title: profile?.title,
        bio: profile?.bio,
        contact: profile?.contact,
        avatar: profile?.avatar,
        gameCharacter: profile?.gameCharacter,
        customAvatarUrl: profile?.customAvatarUrl,
        profileCompleted: profile?.profileCompleted
      }
    } catch (error) {
      return null
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      return await authService.isAuthenticated()
    } catch (error) {
      return false
    }
  }

  async refreshSession(): Promise<boolean> {
    try {
      const user = await authService.checkSession()
      return user !== null
    } catch (error) {
      return false
    }
  }

  async validateEmail(email: string): Promise<boolean> {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  async resetPassword(email: string): Promise<void> {
    throw new Error('Password reset not implemented in enhanced auth service')
  }

  async getProfile(userId: string): Promise<UserProfile | null> {
    try {
      const profile = await authService.getCurrentProfile()
      return profile
    } catch (error: any) {
      return null
    }
  }

  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const result = await authService.updateProfile(userId, updates)
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data!
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update profile')
    }
  }

  async createProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const result = await authService.updateProfile(userId, profile)
      if (result.error) {
        throw new Error(result.error)
      }
      return result.data!
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create profile')
    }
  }
} 